import { useMutation, useQueryClient } from '@tanstack/react-query';
import { T, useGT } from 'gt-react';
import type { PricelistCaseResponse } from '@erp/rpc';
import { orpc } from '@/api/rpc/client';
import { useToast } from 'src/context/ToastContext';
import { Label } from '@/components/ui/label';
import { Watchers } from '@/components/watchers/Watchers';
import { PricelistCaseNotifyEmailsField } from './PricelistCaseNotifyEmailsField';
import {
  refreshPricelistCaseViews,
  rememberPricelistCase,
} from './pricelistCaseQueryCache';

export function PricelistCaseAudienceSection({
  pricelistCase,
}: {
  pricelistCase: PricelistCaseResponse;
}) {
  const gt = useGT();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const updateMutation = useMutation({
    ...orpc.inventoryManagement.pricelistCases.update.mutationOptions(),
    onSuccess: (updated) => rememberPricelistCase(queryClient, updated),
  });
  const disabled = updateMutation.isPending || pricelistCase.task.taskDeleted;

  async function persistNotifyEmails(notifyEmails: string[]) {
    try {
      await updateMutation.mutateAsync({
        caseId: pricelistCase.id,
        details: {},
        notifyEmails,
      });
      refreshPricelistCaseViews(queryClient);
    } catch (error) {
      showToast({
        title: gt('Could not update follow-up emails', {
          context:
            'toast title when saving extra follow-up emails on a price change fails',
        }),
        message:
          error instanceof Error
            ? error.message
            : gt('Something went wrong.', {
                context: 'generic toast message for an unexpected failure',
              }),
        color: 'error',
      });
    }
  }

  return (
    <>
      {!pricelistCase.task.taskDeleted ? (
        <div className="space-y-1.5">
          <Label className="text-xs">
            <T context="field label for people watching a pricelist update case">
              Watchers
            </T>
          </Label>
          <Watchers
            watchableEntityType="task"
            watchableId={pricelistCase.task.id}
            size="sm"
          />
        </div>
      ) : undefined}
      <PricelistCaseNotifyEmailsField
        emails={pricelistCase.notifyEmails}
        disabled={disabled}
        onChange={(emails) => void persistNotifyEmails(emails)}
      />
    </>
  );
}
